import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card.tsx';
import { Alert } from '../ui/alert.tsx';
import { CheckCircle2, XCircle, Sparkles } from 'lucide-react';
import Editor from '@monaco-editor/react';
import type { editor } from 'monaco-editor';
import CONFIG from '../../config.js';
import config_names from '../../config_names.js';
import { supabase } from '../../supabase';

interface ValidationResult {
  valid: boolean;
  error?: string;
  position?: number;
  hint?: string;
}

interface DebugValueResult {
  symbol?: string;
  screen_date?: string;
  value?: number | null;
  price?: number | null;
  message?: string;
  error?: string;
}

interface FormulaEditorProps {
  value: string;
  onChange: (formula: string) => void;
  onValidate?: (result: ValidationResult) => void;
  apiBaseUrl?: string;
}

const FormulaEditor: React.FC<FormulaEditorProps> = ({
  value,
  onChange,
  onValidate,
  apiBaseUrl = CONFIG.API_BASE
}) => {
  const [formula, setFormula] = useState(value);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [debugValue, setDebugValue] = useState<DebugValueResult | null>(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
  const validationTimeoutRef = useRef<NodeJS.Timeout>();
  const debugRequestRef = useRef(0);
  const guideUrl = `${config_names.domain}/formula-guide`;

  const formatDebugNumber = (value?: number | null) => {
    if (value === null || value === undefined || !Number.isFinite(value)) {
      return 'N/A';
    }
    const abs = Math.abs(value);
    if (abs > 1000) return value.toFixed(2);
    if (abs > 0 && abs < 0.01) return value.toFixed(6);
    return value.toFixed(4);
  };

  const formatDebugPrice = (value?: number | null) => {
    if (value === null || value === undefined || !Number.isFinite(value)) {
      return 'N/A';
    }
    return `$${value.toFixed(2)}`;
  };

  const fetchDebugValue = useCallback(async (formulaText: string) => {
    if (!formulaText.trim()) {
      setDebugValue(null);
      return;
    }

    const requestId = ++debugRequestRef.current;
    setDebugLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        if (debugRequestRef.current === requestId) {
          setDebugValue({ error: 'Sign in to preview a live value.' });
          setDebugLoading(false);
        }
        return;
      }

      const response = await fetch(`${apiBaseUrl}/api/formulas/debug`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ formula: formulaText })
      });

      const data = await response.json();
      if (debugRequestRef.current !== requestId) return;

      if (!response.ok || data.status !== 'success') {
        setDebugValue({ error: data.message || 'Failed to load debug value.' });
      } else {
        setDebugValue(data);
      }
    } catch (err) {
      if (debugRequestRef.current !== requestId) return;
      console.error('Debug value error:', err);
      setDebugValue({ error: 'Failed to load debug value.' });
    } finally {
      if (debugRequestRef.current === requestId) {
        setDebugLoading(false);
      }
    }
  }, [apiBaseUrl]);

  // Debounced validation
  const validateFormula = useCallback(async (formulaText: string) => {
    if (!formulaText.trim()) {
      setValidation({ valid: true });
      setDebugValue(null);
      return;
    }

    try {
      const response = await fetch(`${apiBaseUrl}/api/formulas/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ formula: formulaText })
      });

      const data = await response.json();
      const result = data.status === 'success' ? data : { valid: false, error: data.message };

      setValidation(result);
      if (onValidate) {
        onValidate(result);
      }
      if (result.valid) {
        fetchDebugValue(formulaText);
      } else {
        setDebugValue(null);
      }

      // Highlight error position in editor
      if (!result.valid && result.position !== undefined && editorRef.current) {
        const model = editorRef.current.getModel();
        if (model) {
          const position = model.getPositionAt(result.position);
          editorRef.current.revealPositionInCenter(position);
          editorRef.current.setPosition(position);
        }
      }
    } catch (err) {
      console.error('Validation error:', err);
      setValidation({ valid: false, error: 'Failed to validate formula' });
      setDebugValue(null);
    }
  }, [apiBaseUrl, onValidate, fetchDebugValue]);

  // Debounce validation on change
  useEffect(() => {
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }

    validationTimeoutRef.current = setTimeout(() => {
      validateFormula(formula);
    }, 500);

    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
    };
  }, [formula, validateFormula]);

  const handleEditorChange = (value: string | undefined) => {
    const newFormula = value || '';
    setFormula(newFormula);
    setDebugValue(null);
    setDebugLoading(false);
    onChange(newFormula);
  };

  const handleEditorMount = (editor: editor.IStandaloneCodeEditor) => {
    editorRef.current = editor;

    // Configure autocomplete
    const monaco = (window as any).monaco;
    if (monaco) {
      // Register completion provider
      monaco.languages.registerCompletionItemProvider('plaintext', {
        provideCompletionItems: (model: any, position: any) => {
          const word = model.getWordUntilPosition(position);
          const range = {
            startLineNumber: position.lineNumber,
            endLineNumber: position.lineNumber,
            startColumn: word.startColumn,
            endColumn: word.endColumn
          };

          // Metric suggestions
          const metrics = [
            { label: 'price', detail: 'Current stock price', insertText: 'price' },
            { label: 'eps.ttm(0)', detail: 'Earnings per share (TTM)', insertText: 'eps.ttm(0)' },
            { label: 'eps.ttm(4)', detail: 'EPS from ~1 year ago', insertText: 'eps.ttm(4)' },
            { label: 'revenue.ttm(0)', detail: 'Revenue (TTM)', insertText: 'revenue.ttm(0)' },
            { label: 'fcf.ttm(0)', detail: 'Free cash flow (TTM)', insertText: 'fcf.ttm(0)' },
            { label: 'ocf.ttm(0)', detail: 'Operating cash flow (TTM)', insertText: 'ocf.ttm(0)' },
            { label: 'totalAssets.mrq(0)', detail: 'Total assets (MRQ)', insertText: 'totalAssets.mrq(0)' },
            { label: 'totalDebt.mrq(0)', detail: 'Total debt (MRQ)', insertText: 'totalDebt.mrq(0)' },
            { label: 'shares.mrq(0)', detail: 'Shares outstanding (MRQ)', insertText: 'shares.mrq(0)' },
            { label: 'currentRatio.mrq(0)', detail: 'Current ratio', insertText: 'currentRatio.mrq(0)' },
            { label: 'roe.ttm(0)', detail: 'Return on equity', insertText: 'roe.ttm(0)' },
            { label: 'roa.ttm(0)', detail: 'Return on assets', insertText: 'roa.ttm(0)' },
          ];

          // Function suggestions
          const functions = [
            { label: 'min(a, b)', detail: 'Minimum of two values', insertText: 'min($1, $2)' },
            { label: 'max(a, b)', detail: 'Maximum of two values', insertText: 'max($1, $2)' },
            { label: 'round(x)', detail: 'Round to nearest integer', insertText: 'round($1)' },
            { label: 'floor(x)', detail: 'Round down', insertText: 'floor($1)' },
            { label: 'ceil(x)', detail: 'Round up', insertText: 'ceil($1)' },
            { label: 'if(cond, a, b)', detail: 'Conditional expression', insertText: 'if($1, $2, $3)' },
            { label: 'days_since(event)', detail: 'Days since a named event', insertText: 'days_since($1)' },
            { label: 'sma(n)', detail: 'Simple moving average of price', insertText: 'sma($1)' },
            { label: 'momentum(n)', detail: 'Price return over n trading days', insertText: 'momentum($1)' },
            { label: 'momentum(n, skip)', detail: 'Momentum excluding recent days', insertText: 'momentum($1, $2)' },
            { label: 'volatility(n)', detail: 'Std dev of daily returns', insertText: 'volatility($1)' },
            { label: 'rolling_max(n)', detail: 'Highest price in lookback window', insertText: 'rolling_max($1)' },
            { label: 'context(ASSET:SYMBOL, expr)', detail: 'Evaluate expr on another symbol', insertText: 'context($1, $2)' },
            { label: 'rank(expr)', detail: 'Percentile rank across universe', insertText: 'rank($1)' },
            { label: 'zscore(expr)', detail: 'Z-score across universe', insertText: 'zscore($1)' },
          ];

          const suggestions = [...metrics, ...functions].map(item => ({
            label: item.label,
            kind: monaco.languages.CompletionItemKind.Function,
            detail: item.detail,
            insertText: item.insertText,
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            range: range
          }));

          return { suggestions };
        }
      });
    }
  };

  return (
    <div className="formula-editor-container space-y-4">
      {/* Editor */}
      <div className="border-2 border-gray-300 rounded-lg overflow-hidden bg-white">
        <Editor
          height="70px"
          defaultLanguage="plaintext"
          value={formula}
          onChange={handleEditorChange}
          onMount={handleEditorMount}
          theme="vs-light"
          options={{
            minimap: { enabled: false },
            lineNumbers: 'off',
            glyphMargin: false,
            folding: false,
            lineDecorationsWidth: 0,
            lineNumbersMinChars: 0,
            fontSize: 18,
            fontFamily: 'JetBrains Mono, Menlo, Monaco, Courier New, monospace',
            padding: { top: 20, bottom: 20 },
            scrollBeyondLastLine: false,
            wordWrap: 'off',
            suggestOnTriggerCharacters: true,
            quickSuggestions: true,
            scrollbar: {
              vertical: 'hidden',
              horizontal: 'auto',
            },
            overviewRulerLanes: 0,
          }}
        />
      </div>

      {/* Validation status */}
      {validation && !validation.valid && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <div>
            <div className="font-semibold">Syntax Error</div>
            <div className="text-sm">{validation.error}</div>
            {validation.hint && (
              <div className="text-sm mt-1 opacity-80">
                <strong>Hint:</strong> {validation.hint}
              </div>
            )}
          </div>
        </Alert>
      )}

      {validation && validation.valid && formula.trim() && (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <div className="text-green-800">
            <strong>Valid formula</strong> - Ready to use
          </div>
        </Alert>
      )}

      {validation && validation.valid && formula.trim() && (debugLoading || debugValue) && (
        <Alert className="bg-slate-50 border-slate-200">
          <div className="text-slate-800 space-y-1">
            <div className="font-semibold">Latest debug value</div>
            {debugLoading ? (
              <div className="text-sm text-slate-600">Fetching latest value...</div>
            ) : debugValue?.error ? (
              <div className="text-sm text-red-700">{debugValue.error}</div>
            ) : (
              <div className="text-sm text-slate-700">
                <div>
                  {debugValue?.symbol || 'AAPL'}: {formatDebugNumber(debugValue?.value)}
                </div>
                <div className="text-xs text-slate-500">
                  {debugValue?.screen_date ? `As of ${debugValue.screen_date}` : 'Latest available date'}
                  {debugValue?.price !== undefined && debugValue?.price !== null
                    ? ` - Price ${formatDebugPrice(debugValue.price)}`
                    : ''}
                </div>
                {debugValue?.message && (
                  <div className="text-xs text-slate-500">{debugValue.message}</div>
                )}
              </div>
            )}
          </div>
        </Alert>
      )}

      {/* Help text */}
      <Card className="bg-blue-50 border-blue-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-blue-600" />
            Formula Syntax Guide
          </CardTitle>
        </CardHeader>
      <CardContent className="text-sm text-gray-700 space-y-2">
        <div>
          <strong>Metrics:</strong> <code className="bg-white px-1 py-0.5 rounded">eps.ttm(0)</code>,
          <code className="bg-white px-1 py-0.5 rounded ml-1">revenue.ttm(0)</code>,
          <code className="bg-white px-1 py-0.5 rounded ml-1">book_value_per_share.mrq(0)</code>,
          <code className="bg-white px-1 py-0.5 rounded ml-1">price</code>
        </div>
        <div>
          <strong>Operators:</strong> <code className="bg-white px-1 py-0.5 rounded">+ - * / ^ &gt; &lt; &gt;= &lt;= == != &amp;&amp; || !</code>
        </div>
        <div>
          <strong>Functions:</strong> <code className="bg-white px-1 py-0.5 rounded">sma(n)</code>,
          <code className="bg-white px-1 py-0.5 rounded ml-1">momentum(n, skip)</code>,
          <code className="bg-white px-1 py-0.5 rounded ml-1">volatility(n)</code>,
          <code className="bg-white px-1 py-0.5 rounded ml-1">rolling_max(n)</code>,
          <code className="bg-white px-1 py-0.5 rounded ml-1">min(a, b)</code>,
          <code className="bg-white px-1 py-0.5 rounded ml-1">max(a, b)</code>
        </div>
        <div>
          <strong>Logic:</strong> <code className="bg-white px-1 py-0.5 rounded">if(condition, a, b)</code>
        </div>
        <div>
          <strong>Events:</strong> <code className="bg-white px-1 py-0.5 rounded">days_since(BITCOIN_HALVING)</code>
        </div>
        <div>
          <strong>Context:</strong> <code className="bg-white px-1 py-0.5 rounded">context(STOCKS:AAPL, price)</code>
        </div>
        <div>
          <strong>Transforms:</strong> <code className="bg-white px-1 py-0.5 rounded">rank(expr)</code>,
          <code className="bg-white px-1 py-0.5 rounded ml-1">zscore(expr)</code>
        </div>
        <div className="text-xs text-gray-600">
          Examples: <code className="bg-white px-1 py-0.5 rounded">price / eps.ttm(0)</code> (P/E),
          <code className="bg-white px-1 py-0.5 rounded ml-1">momentum(252, 21)</code> (12m-1m momentum)
        </div>
        <div>
          <a
            href={guideUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center text-blue-700 font-semibold hover:text-blue-900"
          >
            View full DSL guide
          </a>
        </div>
      </CardContent>
      </Card>
    </div>
  );
};

export default FormulaEditor;
